import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import type { GatekeeperFlag } from 'src/context/Gatekeeper'
import {
  useIndexPageColumnSettings,
  useResetIndexPageColumns,
  useSetIndexPageColumnVisibility,
} from 'src/pages/AccountSettings/AccountSettings.hooks'

export type ColumnConfigurablePage =
  | INDEX_PAGE_WITH_FILTERS.CONTROLS
  | INDEX_PAGE_WITH_FILTERS.POLICIES
  | INDEX_PAGE_WITH_FILTERS.RISKS
  | INDEX_PAGE_WITH_FILTERS.VENDORS

export const CONFIGURABLE_COLUMN_PAGES: ColumnConfigurablePage[] = [
  INDEX_PAGE_WITH_FILTERS.CONTROLS,
  INDEX_PAGE_WITH_FILTERS.POLICIES,
  INDEX_PAGE_WITH_FILTERS.RISKS,
  INDEX_PAGE_WITH_FILTERS.VENDORS,
]

export const LOCKED_COLUMNS: Record<ColumnConfigurablePage, readonly string[]> =
  {
    [INDEX_PAGE_WITH_FILTERS.CONTROLS]: ['name'],
    [INDEX_PAGE_WITH_FILTERS.POLICIES]: ['name'],
    [INDEX_PAGE_WITH_FILTERS.RISKS]: ['threat'],
    [INDEX_PAGE_WITH_FILTERS.VENDORS]: ['name'],
  }

export const isColumnLocked = (
  page: ColumnConfigurablePage | string,
  columnKey: string,
): boolean => {
  const pageKey = page as ColumnConfigurablePage
  const locked = LOCKED_COLUMNS[pageKey]
  if (locked?.includes(columnKey)) {
    return true
  }
  return Boolean(
    PAGE_COLUMNS[pageKey]?.find((c) => c.key === columnKey)?.isLocked,
  )
}

export interface ColumnDefinition {
  key: string
  label: string
  isLocked?: boolean
  gatekeeperFlag?: GatekeeperFlag
}

export const PAGE_COLUMNS: Record<ColumnConfigurablePage, ColumnDefinition[]> =
  {
    [INDEX_PAGE_WITH_FILTERS.CONTROLS]: [
      { key: 'department', label: 'Department' },
      { key: 'id', label: 'Control ID' },
      { key: 'name', label: 'Control Name', isLocked: true },
      {
        key: 'labels',
        label: 'Labels',
        gatekeeperFlag: 'labels' as GatekeeperFlag,
      },
      { key: 'risks', label: 'Risks' },
      { key: 'frameworks', label: 'Frameworks' },
      { key: 'evidence', label: 'Linked Evidence' },
      { key: 'operatingEffectiveness', label: 'Operating Effectiveness' },
      { key: 'status', label: 'Status' },
      { key: 'owner', label: 'Owner' },
      { key: 'assignees', label: 'Assignees' },
    ],
    [INDEX_PAGE_WITH_FILTERS.POLICIES]: [
      { key: 'department', label: 'Department' },
      { key: 'name', label: 'Policy Name', isLocked: true },
      {
        key: 'labels',
        label: 'Labels',
        gatekeeperFlag: 'labels' as GatekeeperFlag,
      },
      { key: 'frameworks', label: 'Frameworks' },
      { key: 'documentation', label: 'Documentation' },
      { key: 'lastReviewed', label: 'Last Reviewed' },
      { key: 'lastApproved', label: 'Last Approved' },
      { key: 'controls', label: 'Controls in Scope' },
      { key: 'owner', label: 'Owner' },
      { key: 'assignees', label: 'Assignees' },
    ],
    [INDEX_PAGE_WITH_FILTERS.RISKS]: [
      { key: 'department', label: 'Department' },
      { key: 'id', label: 'Risk ID' },
      { key: 'threat', label: 'Threat', isLocked: true },
      { key: 'category', label: 'Category' },
      { key: 'controls', label: 'Controls' },
      { key: 'inherentRisk', label: 'Inherent Risk' },
      { key: 'targetResidualRisk', label: 'Target Residual Risk' },
      { key: 'currentResidualRisk', label: 'Current Residual Risk' },
      { key: 'lastReviewed', label: 'Last Reviewed' },
      { key: 'status', label: 'Status' },
      { key: 'owner', label: 'Owner' },
      { key: 'assignees', label: 'Assignees' },
    ],
    [INDEX_PAGE_WITH_FILTERS.VENDORS]: [
      { key: 'name', label: 'Vendor Name', isLocked: true },
      { key: 'riskTier', label: 'Risk Tier' },
      { key: 'status', label: 'Status' },
      { key: 'state', label: 'State' },
      { key: 'attestation', label: 'Attestation' },
      { key: 'relatedRisks', label: 'Related Risks' },
      { key: 'owner', label: 'Owner' },
      { key: 'assignees', label: 'Assignees' },
      { key: 'lastReviewed', label: 'Last Reviewed' },
    ],
  }

export type ColumnHideListState = {
  [page in ColumnConfigurablePage]?: Record<string, boolean>
}

export interface ColumnHideListContextValue {
  hideList: ColumnHideListState
  isColumnHidden: (page: string, columnKey: string) => boolean
  /**
   * Applies the change optimistically and resolves once it is persisted.
   * Rejects - after rolling the optimistic change back - when the write fails,
   * so callers can report the failure instead of claiming success.
   */
  setColumnHidden: (
    page: string,
    columnKey: string,
    hidden: boolean,
  ) => Promise<void>
  resetPageColumns: (page: string) => Promise<void>
  resetAllColumns: () => Promise<void>
  /**
   * Counts hidden columns of `page`, restricted to `columnKeys` when given so
   * callers can ignore persisted keys for columns that are not currently shown
   * (e.g. gatekeeper-disabled).
   */
  getHiddenColumnCount: (page: string, columnKeys?: readonly string[]) => number
  isLoading: boolean
}

const ColumnHideListContext = createContext<ColumnHideListContextValue>({
  hideList: {},
  isColumnHidden: () => false,
  setColumnHidden: () => Promise.resolve(),
  resetPageColumns: () => Promise.resolve(),
  resetAllColumns: () => Promise.resolve(),
  getHiddenColumnCount: () => 0,
  isLoading: false,
})

const ALL_PAGES_WRITE_KEY = '*'

const withColumnHidden = (
  list: ColumnHideListState,
  page: ColumnConfigurablePage,
  columnKey: string,
  hidden: boolean,
): ColumnHideListState => {
  const pageList = { ...(list[page] || {}) }
  if (hidden) {
    pageList[columnKey] = true
  } else {
    delete pageList[columnKey]
  }
  return { ...list, [page]: pageList }
}

const withoutPage = (
  list: ColumnHideListState,
  page: ColumnConfigurablePage,
): ColumnHideListState => {
  const next = { ...list }
  delete next[page]
  return next
}

export const ColumnHideListProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const { data, loading: isLoading } = useIndexPageColumnSettings()
  const setVisibilityApi = useSetIndexPageColumnVisibility()
  const resetColumnsApi = useResetIndexPageColumns()

  const [localHideList, setLocalHideList] = useState<ColumnHideListState>({})
  const hideListRef = useRef<ColumnHideListState>(localHideList)
  // Last state the server confirmed. Failed writes roll back to this rather than
  // to the state before their own optimistic update, which may itself have been
  // an unpersisted optimistic value.
  const persistedRef = useRef<ColumnHideListState>({})
  const pendingWrites = useRef<Map<string, Promise<void>>>(new Map())
  const latestWriteIds = useRef<Map<string, number>>(new Map())
  const writeCounter = useRef(0)

  const applyHideList = useCallback((next: ColumnHideListState) => {
    hideListRef.current = next
    setLocalHideList(next)
  }, [])

  const applyColumnHidden = useCallback(
    (page: ColumnConfigurablePage, columnKey: string, hidden: boolean) => {
      applyHideList(
        withColumnHidden(hideListRef.current, page, columnKey, hidden),
      )
    },
    [applyHideList],
  )

  // Records `key`'s newest write and returns a check for whether a given write is
  // still the newest - a superseded write must not roll back its successor.
  const trackLatestWrite = useCallback((key: string): (() => boolean) => {
    const id = ++writeCounter.current
    latestWriteIds.current.set(key, id)
    return () => latestWriteIds.current.get(key) === id
  }, [])

  const queueWrite = useCallback(
    (key: string, write: () => Promise<void>): Promise<void> => {
      const previous = pendingWrites.current.get(key) ?? Promise.resolve()
      const next = previous.catch(() => undefined).then(write)
      pendingWrites.current.set(key, next)
      next
        .catch(() => undefined)
        .then(() => {
          if (pendingWrites.current.get(key) === next) {
            pendingWrites.current.delete(key)
          }
        })
      return next
    },
    [],
  )

  useEffect(() => {
    if (!data) return
    const list: ColumnHideListState = {}
    const items = data.getItemsList()
    items.forEach((item) => {
      const page = item.getPage() as ColumnConfigurablePage
      const columnKey = item.getColumnKey()
      const hidden = item.getHidden()
      if (hidden) {
        const pageMap = list[page] ?? {}
        pageMap[columnKey] = true
        list[page] = pageMap
      }
    })
    persistedRef.current = list
    applyHideList(list)
  }, [data, applyHideList])

  const isColumnHidden = useCallback(
    (page: string, columnKey: string): boolean => {
      const pageKey = page as ColumnConfigurablePage
      if (isColumnLocked(pageKey, columnKey)) {
        return false
      }
      return Boolean(localHideList[pageKey]?.[columnKey])
    },
    [localHideList],
  )

  const setColumnHidden = useCallback(
    (page: string, columnKey: string, hidden: boolean): Promise<void> => {
      const pageKey = page as ColumnConfigurablePage
      if (isColumnLocked(pageKey, columnKey)) {
        return Promise.resolve()
      }

      const writeKey = `${page}:${columnKey}`
      const isLatest = trackLatestWrite(writeKey)
      applyColumnHidden(pageKey, columnKey, hidden)

      return queueWrite(writeKey, () =>
        setVisibilityApi(page, columnKey, hidden),
      ).then(
        () => {
          persistedRef.current = withColumnHidden(
            persistedRef.current,
            pageKey,
            columnKey,
            hidden,
          )
        },
        (err) => {
          if (isLatest()) {
            applyColumnHidden(
              pageKey,
              columnKey,
              Boolean(persistedRef.current[pageKey]?.[columnKey]),
            )
          }
          throw err
        },
      )
    },
    [applyColumnHidden, queueWrite, setVisibilityApi, trackLatestWrite],
  )

  const resetPageColumns = useCallback(
    (page: string): Promise<void> => {
      const pageKey = page as ColumnConfigurablePage
      const isLatest = trackLatestWrite(page)
      applyHideList(withoutPage(hideListRef.current, pageKey))

      return queueWrite(page, () => resetColumnsApi(page)).then(
        () => {
          persistedRef.current = withoutPage(persistedRef.current, pageKey)
        },
        (err) => {
          if (isLatest()) {
            applyHideList({
              ...hideListRef.current,
              [pageKey]: persistedRef.current[pageKey] ?? {},
            })
          }
          throw err
        },
      )
    },
    [applyHideList, queueWrite, resetColumnsApi, trackLatestWrite],
  )

  const resetAllColumns = useCallback((): Promise<void> => {
    const isLatest = trackLatestWrite(ALL_PAGES_WRITE_KEY)
    applyHideList({})

    return queueWrite(ALL_PAGES_WRITE_KEY, () => resetColumnsApi()).then(
      () => {
        persistedRef.current = {}
      },
      (err) => {
        if (isLatest()) {
          applyHideList(persistedRef.current)
        }
        throw err
      },
    )
  }, [applyHideList, queueWrite, resetColumnsApi, trackLatestWrite])

  const getHiddenColumnCount = useCallback(
    (page: string, columnKeys?: readonly string[]): number => {
      const pageKey = page as ColumnConfigurablePage
      const pageList = localHideList[pageKey]
      if (!pageList) return 0
      const keys = columnKeys ?? Object.keys(pageList)
      return keys.filter((key) => isColumnHidden(pageKey, key)).length
    },
    [localHideList, isColumnHidden],
  )

  const value = useMemo(
    () => ({
      hideList: localHideList,
      isColumnHidden,
      setColumnHidden,
      resetPageColumns,
      resetAllColumns,
      getHiddenColumnCount,
      isLoading,
    }),
    [
      localHideList,
      isColumnHidden,
      setColumnHidden,
      resetPageColumns,
      resetAllColumns,
      getHiddenColumnCount,
      isLoading,
    ],
  )

  return (
    <ColumnHideListContext.Provider value={value}>
      {children}
    </ColumnHideListContext.Provider>
  )
}

export const useColumnHideList = (): ColumnHideListContextValue =>
  useContext(ColumnHideListContext)

// The Policies, Risks and Vendors grids are reused on detail pages (requests,
// risks, vendors) for linked records. Account-wide column settings belong to the
// index pages only, so each index page opts its grid in with this scope.
const ColumnSettingsScopeContext = createContext<ColumnConfigurablePage | null>(
  null,
)

export const ColumnSettingsScope = ({
  page,
  children,
}: {
  page: ColumnConfigurablePage
  children: React.ReactNode
}): JSX.Element => (
  <ColumnSettingsScopeContext.Provider value={page}>
    {children}
  </ColumnSettingsScopeContext.Provider>
)

/**
 * Returns a predicate reporting whether a column of `page` is hidden - always
 * false outside a matching ColumnSettingsScope.
 */
export const useScopedColumnHidden = (
  page: ColumnConfigurablePage,
): ((columnKey: string) => boolean) => {
  const scope = useContext(ColumnSettingsScopeContext)
  const { isColumnHidden } = useColumnHideList()
  const isInScope = scope === page
  return useCallback(
    (columnKey: string) => isInScope && isColumnHidden(page, columnKey),
    [isInScope, isColumnHidden, page],
  )
}
